import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { api } from "@/lib/api";
import { shareGame } from "@/lib/share";
import { ConnectorIcon, CONNECTOR_LETTER, SHIELD_BG, SHIELD_LETTER, tilesText } from "@/components/TileArt";
import {
  Play, Pause, SkipBack, SkipForward, Rewind, FastForward, Trophy, Flag, ShareNetwork,
} from "@phosphor-icons/react";

// Fallback board (the old classic shape) when the game board can't be read.
const DEFAULT_BOARD = {
  rows: 20, cols: 13, spec: {},
  base: {
    H: [4, 5, 6, 7, 8].map((c) => [19, c]),
    A: [4, 5, 6, 7, 8].map((c) => [0, c]),
  },
};

// The same colours as the game board: you are pink at the bottom, your
// opponent blue at the top.
const ME = { tile: "#ee7e9a", base: "rgba(238,126,154,0.35)" };
const OPP = { tile: "#3d9be0", base: "rgba(61,155,224,0.35)" };
const SPEC_BG = {
  WALL: "rgba(148,163,184,0.75)",
  PINK: "rgba(244,114,182,0.18)",
  GREEN: "rgba(52,211,153,0.2)",
  // Gem squares (SHIELD) are left out: the review is of a finished game.
  CONNECTOR: "rgba(250,204,21,0.18)",
};

const SPEEDS = [
  { label: "0.5×", ms: 2400 },
  { label: "1×", ms: 1200 },
  { label: "2×", ms: 600 },
  { label: "4×", ms: 250 },
];

/**
 * Turn a move log into one step per turn: the board after each player's
 * play, with the tiles that turn added. Moves are { actor: "me"|"opp",
 * action, detail: {r, c, letter} } in the mover's own view (their base at
 * the bottom), so the opponent's are flipped to the top.
 */
export function buildSteps(moves, rows, opts = {}) {
  const flip = (r) => rows - 1 - r;
  const steps = [{ tiles: {}, added: [], actor: null, kind: "start" }];
  let tiles = {};
  let pending = false;
  let lastActor = null;
  const close = (kind) => {
    const prev = steps[steps.length - 1].tiles;
    // Only the closing board (Jack, 2026-11-03): a game that ends on time
    // can leave loose letters there. Earlier turns stay exactly as played,
    // as in the live app (dropping groups there hid opening moves, so the
    // forward arrow showed empty boards).
    // Stream only for now (Jack, 2026-11-04); the normal game is unchanged.
    if (opts.dropLoose && (kind === "final" || kind === "end")) tiles = dropStrays(tiles, prev, lastActor, opts);
    const added = Object.keys(tiles).filter((k) => !prev[k] || prev[k].letter !== tiles[k].letter || prev[k].color !== tiles[k].color);
    steps.push({ tiles: { ...tiles }, added, actor: lastActor, kind });
    pending = false;
  };
  for (const m of moves || []) {
    const d = m.detail || {};
    const mine = m.actor === "me";
    if (m.action === "peel") {
      lastActor = m.actor;
      close("turn");
      continue;
    }
    const r = Number(d.r), c = Number(d.c);
    if (!Number.isFinite(r) || !Number.isFinite(c)) continue;
    const key = `${mine ? r : flip(r)},${c}`;
    if (m.action === "place") {
      tiles = { ...tiles, [key]: { color: mine ? "me" : "opp", letter: String(d.letter || "").toUpperCase() } };
    } else if (m.action === "pickup") {
      const { [key]: _gone, ...rest } = tiles;
      tiles = rest;
    } else if (m.action === "cut") {
      // A cut removes the other player's tile at the cutter's (r, c).
      const { [key]: _cut, ...rest } = tiles;
      tiles = rest;
    } else {
      continue;
    }
    lastActor = m.actor;
    pending = true;
  }
  // The winning move usually ends the game before the turn is played.
  if (pending) close("final");
  // A game that ended on a played turn: tidy its last board the same way.
  else if (opts.dropLoose && steps.length > 1) {
    const last = steps[steps.length - 1];
    const before = steps[steps.length - 2].tiles;
    const kept = dropStrays(last.tiles, before, last.actor, opts);
    if (kept !== last.tiles) steps[steps.length - 1] = { ...last, tiles: kept, added: last.added.filter((k) => kept[k]) };
  }
  return steps;
}

// Letters a turn left lying loose (Jack, 2026-11-03): when a turn ends on
// time, tiles that never joined the chain go back to the hand in the game,
// but the move log could still hold them, so the review showed letters on
// their own. A group of this turn's letters that touches no earlier tile
// (and no square of the mover's base, when the board's bases are known)
// isn't part of any chain, so it is left out. Walls and other non-letter
// tiles always stay. Before a player's first tile, with no base known,
// nothing is dropped.
const LETTER = /^[A-Z]$/;
function dropStrays(tiles, prev, actor, { base, cols, wrap } = {}) {
  if (!actor) return tiles;
  const fresh = Object.keys(tiles).filter((k) => tiles[k].color === actor && LETTER.test(tiles[k].letter)
    && !(prev[k] && prev[k].letter === tiles[k].letter && prev[k].color === actor));
  if (!fresh.length) return tiles;
  const freshSet = new Set(fresh);
  const homes = new Set(((base && base[actor === "me" ? "H" : "A"]) || []).map(([r, c]) => `${r},${c}`));
  const hasOld = Object.keys(tiles).some((k) => !freshSet.has(k));
  if (!homes.size && !Object.keys(tiles).some((k) => tiles[k].color === actor && !freshSet.has(k))) return tiles;
  const near = (k) => {
    const [r, c] = k.split(",").map(Number);
    return [[r - 1, c], [r + 1, c], [r, c - 1], [r, c + 1]].map(([nr, nc]) => {
      if (wrap && cols) nc = (nc + cols) % cols;
      return `${nr},${nc}`;
    });
  };
  const seen = new Set();
  let out = tiles;
  for (const start of fresh) {
    if (seen.has(start)) continue;
    const group = [start];
    seen.add(start);
    let anchored = false;
    for (let i = 0; i < group.length; i++) {
      const k = group[i];
      if (homes.has(k)) anchored = true;
      for (const n of near(k)) {
        if (!tiles[n]) continue;
        if (freshSet.has(n)) { if (!seen.has(n)) { seen.add(n); group.push(n); } }
        else if (hasOld) anchored = true;
      }
    }
    if (!anchored) {
      out = { ...out };
      group.forEach((k) => { delete out[k]; });
    }
  }
  return out;
}

// Server moves ({by, a, r, c, l}) in the shape buildSteps takes.
export function fromServerMoves(list, myId) {
  return (list || []).map((m) => ({
    actor: m.by === myId ? "me" : "opp",
    action: m.a,
    detail: { r: m.r, c: m.c, letter: m.l },
  }));
}

export default function ReviewModal({
  open, onOpenChange, moves, matchId, myId, board, meUsername, oppUsername,
  eloDelta, winnerIsMe, onClose, dropLoose = false,
}) {
  const B = board && board.rows ? board : DEFAULT_BOARD;
  // Both players' moves come from the server, which saves them as they're
  // played; this browser's own log is the fallback (bot games, older games).
  const [serverMoves, setServerMoves] = useState(null);
  useEffect(() => {
    if (!open || !matchId) return undefined;
    let alive = true;
    setServerMoves(null);
    api.get(`/match/${matchId}/moves`)
      .then(({ data }) => { if (alive) setServerMoves(fromServerMoves(data?.moves, myId)); })
      .catch(() => { if (alive) setServerMoves([]); });
    return () => { alive = false; };
  }, [open, matchId, myId]);
  const loading = open && matchId && serverMoves === null;
  const useMoves = serverMoves && serverMoves.length ? serverMoves : moves || [];
  const steps = useMemo(() => buildSteps(useMoves, B.rows, { base: B.base, cols: B.cols, wrap: B.wrap, dropLoose }), [useMoves, B, dropLoose]);
  const total = steps.length;
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speedIdx, setSpeedIdx] = useState(1);
  const timerRef = useRef(null);

  // Start at the beginning each time it opens.
  useEffect(() => {
    if (open) { setIdx(0); setPlaying(false); }
  }, [open]);

  useEffect(() => {
    if (!playing) return undefined;
    if (idx >= total - 1) { setPlaying(false); return undefined; }
    timerRef.current = setTimeout(() => setIdx((i) => Math.min(i + 1, total - 1)), SPEEDS[speedIdx].ms);
    return () => clearTimeout(timerRef.current);
  }, [playing, idx, total, speedIdx]);

  const step = steps[Math.min(idx, total - 1)] || steps[0];
  const added = useMemo(() => new Set(step.added), [step]);
  const baseOf = useMemo(() => {
    const m = {};
    (B.base?.H || []).forEach(([r, c]) => { m[`${r},${c}`] = "me"; });
    (B.base?.A || []).forEach(([r, c]) => { m[`${r},${c}`] = "opp"; });
    return m;
  }, [B]);

  const cells = [];
  for (let r = 0; r < B.rows; r++) {
    for (let c = 0; c < B.cols; c++) {
      const key = `${r},${c}`;
      const tile = step.tiles[key];
      const base = baseOf[key];
      const spec = B.spec?.[key];
      const bg = base ? (base === "me" ? ME.base : OPP.base) : SPEC_BG[spec] || "transparent";
      cells.push(
        <div key={key} className="relative aspect-square rounded-[2px]" style={{ background: bg }}>
          {tile && (
            <span
              className={`absolute inset-[6%] rounded-[2px] flex items-center justify-center font-bold text-white leading-none
                ${added.has(key) ? "ring-2 ring-yellow-300 z-10" : ""}`}
              style={{
                background: tile.letter === SHIELD_LETTER ? SHIELD_BG : tile.color === "me" ? ME.tile : OPP.tile,
                fontSize: "clamp(6px, 1.6vw, 10px)",
              }}
            >
              {tile.letter === CONNECTOR_LETTER ? <ConnectorIcon /> : tile.letter === SHIELD_LETTER ? null : tile.letter}
            </span>
          )}
        </div>
      );
    }
  }

  // Count each player's turns so a step reads "Jack · turn 3".
  const turnNo = useMemo(() => {
    let me = 0, opp = 0;
    return steps.map((s) => {
      if (s.kind === "start") return 0;
      return s.actor === "me" ? ++me : ++opp;
    });
  }, [steps]);
  const words = tilesText(step.added.map((k) => step.tiles[k]?.letter));
  const actorName = step.actor === "me" ? meUsername : step.actor === "opp" ? oppUsername : null;
  const actorColour = step.actor === "me" ? ME.tile : OPP.tile;
  let caption = "Start of the game";
  if (step.kind !== "start") {
    caption = step.kind === "final"
      ? `${actorName}'s winning move`
      : `${actorName} · turn ${turnNo[idx]}`;
  }

  const togglePlay = () => {
    if (idx >= total - 1) { setIdx(0); setPlaying(true); }
    else setPlaying((p) => !p);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="review-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black flex items-center gap-2">
            {winnerIsMe
              ? <><Trophy size={22} weight="fill" className="text-brand" /> Game review</>
              : <><Flag size={20} className="text-destructive" /> Game review</>}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {meUsername} vs {oppUsername} ·{" "}
            <span className={typeof eloDelta === "number" && eloDelta >= 0 ? "text-[hsl(var(--chart-4))]" : "text-destructive"}>
              Elo {typeof eloDelta === "number" ? `${eloDelta >= 0 ? "+" : ""}${eloDelta}` : "—"}
            </span>
            {" · "}
            <span className="text-muted-foreground">Step through every turn, yours and theirs.</span>
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-center gap-5 text-xs">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm" style={{ background: OPP.tile }} />
            <span className="font-display font-bold">{oppUsername}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm" style={{ background: ME.tile }} />
            <span className="font-display font-bold">{meUsername}</span>
          </div>
        </div>

        {/* What this step was */}
        <div className="text-center text-sm min-h-[1.5rem]" data-testid="review-caption">
          <span className="font-display font-bold" style={actorName ? { color: actorColour } : undefined}>{caption}</span>
          {words && step.kind !== "start" && (
            <span className="ml-2 font-mono tracking-wider text-foreground/80">+{words}</span>
          )}
        </div>

        <div className="flex items-center justify-center">
          {loading ? (
            <div className="text-sm text-muted-foreground py-16">Loading the game…</div>
          ) : (
            <div
              data-testid="review-board"
              className="grid gap-px bg-border/40 p-px rounded-md"
              style={{
                gridTemplateColumns: `repeat(${B.cols}, minmax(0, 1fr))`,
                // Fits the whole board on screen, phones included.
                width: `min(88vw, 360px, calc(52vh * ${B.cols} / ${B.rows}))`,
              }}
            >
              {cells}
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="rounded-xl border border-border bg-surface/60 p-3 space-y-2">
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground font-mono w-14 text-right tabular-nums" data-testid="review-count">
              {idx + 1} / {total}
            </span>
            <Slider
              value={[idx]}
              min={0}
              max={Math.max(0, total - 1)}
              step={1}
              onValueChange={(v) => { setPlaying(false); setIdx(v[0] ?? 0); }}
              className="flex-1"
              data-testid="review-scrubber"
            />
          </div>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="rounded-full h-8 w-8" aria-label="First turn"
                onClick={() => { setPlaying(false); setIdx(0); }} data-testid="review-start-btn">
                <SkipBack size={14} weight="fill" />
              </Button>
              <Button variant="ghost" size="icon" className="rounded-full h-8 w-8" aria-label="Previous turn"
                onClick={() => { setPlaying(false); setIdx((i) => Math.max(0, i - 1)); }} data-testid="review-prev-btn">
                <Rewind size={14} weight="fill" />
              </Button>
              <Button size="icon" className="rounded-full h-9 w-9 bg-brand hover:bg-brand-hover text-brand-foreground"
                aria-label={playing ? "Pause" : "Play"} onClick={togglePlay} data-testid="review-play-btn">
                {playing ? <Pause size={16} weight="fill" /> : <Play size={16} weight="fill" />}
              </Button>
              <Button variant="ghost" size="icon" className="rounded-full h-8 w-8" aria-label="Next turn"
                onClick={() => { setPlaying(false); setIdx((i) => Math.min(total - 1, i + 1)); }} data-testid="review-next-btn">
                <FastForward size={14} weight="fill" />
              </Button>
              <Button variant="ghost" size="icon" className="rounded-full h-8 w-8" aria-label="Last turn"
                onClick={() => { setPlaying(false); setIdx(total - 1); }} data-testid="review-end-btn">
                <SkipForward size={14} weight="fill" />
              </Button>
            </div>
            <div className="flex items-center gap-1">
              {SPEEDS.map((sp, i) => (
                <Button key={sp.label} size="sm"
                  variant={i === speedIdx ? "default" : "ghost"}
                  onClick={() => setSpeedIdx(i)}
                  className={`rounded-full h-7 px-2.5 text-xs ${i === speedIdx ? "bg-brand hover:bg-brand-hover text-brand-foreground" : ""}`}
                  data-testid={`review-speed-${i}`}>
                  {sp.label}
                </Button>
              ))}
            </div>
          </div>
          {!loading && total <= 1 && (
            <p className="text-xs text-muted-foreground text-center">No moves were saved for this game.</p>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-1">
          {matchId && (
            <Button className="rounded-full gap-1.5" variant="outline" data-testid="review-share-btn"
              onClick={() => shareGame({
                matchId,
                title: `${meUsername} vs ${oppUsername} · Jangle`,
                text: "Step through our Jangle game:",
              })}>
              <ShareNetwork size={15} weight="bold" /> Share
            </Button>
          )}
          <Button className="rounded-full" variant="outline"
            onClick={() => { onOpenChange(false); onClose?.(); }} data-testid="review-close-btn">
            Back to results
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
